import { Transition } from "@headlessui/react";
import React, { Fragment } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDismissOnEscape } from './useDismissOnEscape';

interface SideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  width?: string;
}

const SideDrawer: React.FC<SideDrawerProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  width = "w-96"
}) => {
  const dismissRef = useDismissOnEscape(isOpen, onClose);
  const drawerContent = (
    <Transition show={isOpen} as={Fragment}>
      {/* Backdrop */}
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      </Transition.Child>

      {/* Drawer */}
      <Transition.Child
        as={Fragment}
        enter="transform transition ease-out duration-300"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transform transition ease-in duration-200"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        {/* Width comes from the `width` prop. The viewport max-width keeps a fixed drawer (for example w-96) on screen; do not add another max-w-* here. */}
        <div ref={dismissRef} className={`fixed right-2 top-2 bottom-2 bg-white shadow-xl z-50 flex min-h-0 max-w-[calc(100vw-1rem)] flex-col rounded-xl overflow-hidden ${width}`}>
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between gap-2 p-2 border-b border-gray-200">
            <h2 className="text-sm sm:text-base font-semibold text-gray-900 truncate min-w-0">{title}</h2>
            <button
              onClick={onClose}
              className="p-1 rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Close drawer"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content — min-h-0 required so flex child can scroll on mobile */}
          <div
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-4 [-webkit-overflow-scrolling:touch]"
            style={{ touchAction: 'pan-y' }}
          >
            {children}
          </div>
        </div>
      </Transition.Child>
    </Transition>
  );

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(drawerContent, document.body);
};

export default SideDrawer;

